import { Location } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';

import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { AlertInfo } from '../../../../models/alert-info';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { QualityManagementService } from '../../../../services/quality-management.service';
import { FormService } from '../../../../services/form.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';
import { CompanyDocumentGroupPayload } from '../../../../models/entities/company-document';


@Component({
  selector: 'quality-document-group-form',
  imports: [InputFieldComponent, ReactiveFormsModule, NgbAlert],
  templateUrl: './quality-document-group-form.component.html',
  styleUrl: './quality-document-group-form.component.scss'
})
export class QualityDocumentGroupFormComponent implements OnInit {

  @Input() companyDocumentGroup?: any;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  form!: FormGroup;
  canModify: boolean = false;
  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  constructor(
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private qualityManagementService: QualityManagementService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private location: Location
  ) { }

  ngOnInit(): void {
    this.form = new FormGroup({
      code: new FormControl('', Validators.required),
      name: new FormControl('', Validators.required)
    });

    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      this.canModify = this.accessControlService.hasFeatureActionAccess(
        profileCode,
        'QUALITY_MANAGEMENT_SETTINGS_DOCUMENT_GROUPS',
        'modify'
      );

      if (this.canModify) {
        this.form.enable({ emitEvent: false });
      } else {
        this.form.disable({ emitEvent: false });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyDocumentGroup'] && changes['companyDocumentGroup'].currentValue) {
      this.form?.patchValue({
        code: this.companyDocumentGroup.code,
        name: this.companyDocumentGroup.name
      });
    }
  }

  getCodeInput(): FormControl { return this.getFormControl('code'); }
  getNameInput(): FormControl { return this.getFormControl('name'); }
  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }

  submitForm(): void {
    if (!this.canModify) {
      return;
    }

    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    if (!this.form.valid) {
      return;
    }

    this.alertInfo.show = false;
    this.formService.resetErrors(this.form);

    const values: CompanyDocumentGroupPayload = this.form.getRawValue();
    const companyDocumentGroupId = this.companyDocumentGroup?.companyDocumentGroupId;

    const request$ = companyDocumentGroupId
      ? this.qualityManagementService.updateCompanyDocumentGroup(Number(companyDocumentGroupId), values)
      : this.qualityManagementService.createCompanyDocumentGroup(values);

    request$.subscribe({
      next: (response: any) => this.submitFormSuccess(response),
      error: (error: any) => this.submitFormError(error)
    });
  }

  goBack(): void {
    this.location.back();
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => this.alertInfo.show = false, 3000);
  }

  private submitFormSuccess(response: any): void {
    this.alertNotificationService.success('Operation completed successfully');
    this.showAlertInfo('success', 'Operation completed successfully');

    this.onSavedData.emit({ success: true, response });
  }

  private submitFormError(error: any): void {
    const apiError = error?.error?.error;

    if (apiError?.description === 'ARGUMENT_VALIDATION_ERROR') {
      this.formService.resetErrors(this.form);
      this.formService.setValidationErrors(this.form, apiError.validationErrors);
    } else {
      this.showAlertInfo(
        'danger',
        this.qualityManagementService.companyDocumentErrorMessage(
          apiError,
          apiError?.message ?? 'Failed to save the company document group.'
        )
      );
    }

    this.onSavedData.emit({ success: false, error });
  }
}
